import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import PublicNavbar from '@/components/layout/PublicNavbar';
import PublicFooter from '@/components/layout/PublicFooter';
import { ShieldCheck, CheckCircle2, Search, ArrowRight, FileText, BadgeCheck, FileSignature } from 'lucide-react';

export default function LegalitasPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#FCFAF6] font-sans selection:bg-[#D4AF37] selection:text-white">
      <PublicNavbar />

      <main className="pt-28 md:pt-36 pb-20 overflow-hidden">
        
        {/* Hero Section */}
        <section className="relative px-6 lg:px-12 mb-20 lg:mb-32">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#D4AF37]/5 rounded-full blur-[100px] -z-10 translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#302621]/5 rounded-full blur-[80px] -z-10 -translate-x-1/2 translate-y-1/2"></div>
          
          <div className="container mx-auto max-w-7xl flex flex-col lg:flex-row items-center gap-16 lg:gap-20">
            {/* Text Content */}
            <div className="w-full lg:w-1/2" data-aos="fade-right">
              <span className="inline-block py-1.5 px-4 bg-[#E6D5B8]/40 text-[#8C6208] text-[10px] font-bold tracking-widest uppercase rounded mb-4">
                LAYANAN LEGALITAS
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 leading-[1.15] text-[#302621]">
                Legalitas Produk <br /> <span className="text-[#D4AF37]">Aman & Terjamin</span>
              </h1>
              <p className="text-base md:text-lg text-[#7A695B] mb-10 leading-relaxed max-w-lg font-light">
                Kami memastikan seluruh produk Anda memenuhi standar regulasi pemerintah. Proses pengurusan BPOM, Sertifikasi Halal, dan HAKI menjadi lebih cepat, transparan, dan tanpa ribet.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="bg-[#D4AF37] hover:bg-[#B58B22] text-white px-8 py-6 text-base rounded-lg shadow-lg shadow-[#D4AF37]/20 border-0 transition-transform hover:-translate-y-1">
                  Konsultasi Legalitas <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>

            {/* Image/Visual Content */}
            <div className="w-full lg:w-1/2 relative" data-aos="fade-left">
              <div className="relative rounded-3xl overflow-hidden aspect-[4/3] shadow-2xl">
                <img 
                  src="/Gambar Web/legalitas.png" 
                  alt="Legalitas Dokumen Kosmetik" 
                  className="w-full h-full object-cover scale-105 hover:scale-100 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#302621]/80 via-transparent to-transparent"></div>
              </div>
              
              {/* Floating Badge */}
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-2xl shadow-xl flex items-center gap-4 animate-bounce" style={{ animationDuration: '3s' }}>
                <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37]">
                  <ShieldCheck className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-bold text-[#302621] text-sm">100% Legal</h4>
                  <p className="text-xs text-[#7A695B]">Terdaftar Resmi</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Layanan Utama Legalitas */}
        <section className="py-24 px-6 lg:px-12 bg-white" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl text-center">
            <h2 className="text-3xl font-bold text-[#302621] mb-4">Layanan Legalitas Utama Kami</h2>
            <p className="text-[#7A695B] mb-16 max-w-2xl mx-auto">Kami membantu mengurus tiga pilar utama legalitas untuk memastikan produk Anda aman diedarkan di pasar nasional maupun internasional.</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
              {/* BPOM */}
              <div className="p-8 rounded-3xl bg-[#FCFAF6] border border-[#E6D5B8]/40 hover:border-[#D4AF37] hover:shadow-xl transition-all group">
                <div className="w-14 h-14 rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37] mb-6 group-hover:scale-110 transition-transform">
                  <BadgeCheck className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-[#302621] mb-3">Notifikasi BPOM</h3>
                <p className="text-[#7A695B] leading-relaxed text-sm mb-6">
                  Setiap produk kosmetik wajib memiliki nomor notifikasi BPOM sebelum diedarkan. Kami bantu siapkan Dokumen Informasi Produk (DIP) hingga nomor NA terbit.
                </p>
                <ul className="space-y-2 text-sm text-[#302621]">
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#D4AF37]" /> Review Formula Aman</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#D4AF37]" /> Penyusunan DIP</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#D4AF37]" /> Proses Submit Sistem BPOM</li>
                </ul>
              </div>

              {/* Halal */}
              <div className="p-8 rounded-3xl bg-[#FCFAF6] border border-[#E6D5B8]/40 hover:border-[#D4AF37] hover:shadow-xl transition-all group">
                <div className="w-14 h-14 rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37] mb-6 group-hover:scale-110 transition-transform">
                  <FileText className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-[#302621] mb-3">Sertifikasi Halal</h3>
                <p className="text-[#7A695B] leading-relaxed text-sm mb-6">
                  Tingkatkan kepercayaan konsumen Muslim dengan logo Halal MUI/BPJPH. Kami memastikan seluruh rantai pasok bahan baku sesuai syariat.
                </p>
                <ul className="space-y-2 text-sm text-[#302621]">
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#D4AF37]" /> Audit Bahan Baku</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#D4AF37]" /> SJPH (Sistem Jaminan Produk Halal)</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#D4AF37]" /> Pendampingan Audit</li>
                </ul>
              </div>

              {/* HKI */}
              <div className="p-8 rounded-3xl bg-[#FCFAF6] border border-[#E6D5B8]/40 hover:border-[#D4AF37] hover:shadow-xl transition-all group">
                <div className="w-14 h-14 rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37] mb-6 group-hover:scale-110 transition-transform">
                  <FileSignature className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-[#302621] mb-3">Pendaftaran HKI (Merek)</h3>
                <p className="text-[#7A695B] leading-relaxed text-sm mb-6">
                  Lindungi nama brand Anda dari plagiasi. Kami urus pendaftaran merek dagang Anda di DJKI Kemenkumham agar brand Anda terlindungi secara hukum.
                </p>
                <ul className="space-y-2 text-sm text-[#302621]">
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#D4AF37]" /> Pengecekan Merek</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#D4AF37]" /> Pengajuan ke DJKI</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#D4AF37]" /> Sertifikat Merek</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Mengapa Mengurus Legalitas Bersama Kami */}
        <section className="py-24 px-6 lg:px-12 bg-[#FCFAF6]" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl">
            <div className="flex flex-col lg:flex-row items-center gap-16">
              <div className="w-full lg:w-1/2">
                <h2 className="text-3xl font-bold text-[#302621] mb-6 leading-tight">Proses Pengurusan Cepat & Tanpa Kendala</h2>
                <p className="text-[#7A695B] mb-8 leading-relaxed font-light">Banyak brand owner merasa pusing mengurus legalitas karena birokrasinya. Di Satala Dermatech, Anda tidak perlu repot. Tim regulatori kami sudah sangat berpengalaman menghadapi prosedur pemerintahan.</p>
                
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="mt-1 shrink-0 w-10 h-10 rounded-full bg-white border border-[#D4AF37] flex items-center justify-center text-[#D4AF37]">
                      <Search className="w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="font-bold text-[#302621]">Transparan & Update Berkala</h4>
                      <p className="text-sm text-[#7A695B] mt-1">Status pengajuan dokumen selalu kami update. Anda tidak perlu menebak-nebak sejauh mana progres legalitas produk Anda.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="mt-1 shrink-0 w-10 h-10 rounded-full bg-white border border-[#D4AF37] flex items-center justify-center text-[#D4AF37]">
                      <ShieldCheck className="w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="font-bold text-[#302621]">100% Rahasia Terjamin</h4>
                      <p className="text-sm text-[#7A695B] mt-1">Kami menandatangani NDA (Non-Disclosure Agreement) untuk menjamin seluruh data formula dan identitas brand Anda aman.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-full lg:w-1/2 relative rounded-3xl overflow-hidden aspect-[4/3] shadow-xl">
                 <img 
                  src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=1000&auto=format&fit=crop" 
                  alt="Tim Legalitas" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Proses Pengurusan Legalitas */}
        <section className="py-24 px-6 lg:px-12 bg-white" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl text-center">
            <h2 className="text-3xl font-bold text-[#302621] mb-4">Proses Pengurusan Legalitas</h2>
            <p className="text-[#7A695B] mb-16 max-w-2xl mx-auto">Tahapan sistematis untuk memastikan semua dokumen legalitas Anda selesai tepat waktu.</p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8 relative max-w-4xl mx-auto">
              {[
                { no: "01", title: "Konsultasi Kebutuhan", desc: "Diskusi target market dan penentuan jenis perizinan" },
                { no: "02", title: "Persiapan Dokumen", desc: "Pengumpulan data legalitas perusahaan dan direktur" },
                { no: "03", title: "Review & Validasi", desc: "Pengecekan kesesuaian regulasi formula & desain" },
                { no: "04", title: "Pembuatan Akun", desc: "Registrasi di portal resmi BPOM, Sihalal, atau DJKI" },
                { no: "05", title: "Submit Dokumen", desc: "Proses unggah berkas (DIP) dan persyaratan teknis" },
                { no: "06", title: "Izin Terbit", desc: "Nomor NA BPOM, Sertifikat Halal, atau HKI rilis" }
              ].map((step, idx) => (
                <div key={idx} className="relative z-10 flex flex-col items-center w-full mb-4">
                  <div className="w-12 h-12 rounded-full bg-white border-2 border-[#D4AF37] shadow-sm flex items-center justify-center text-[#8C6208] font-bold text-sm mb-4">
                    {step.no}
                  </div>
                  <h4 className="font-bold text-[#302621] text-sm mb-2 text-center">{step.title}</h4>
                  <p className="text-[11px] text-[#7A695B] text-center leading-relaxed px-2">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Persyaratan Dokumen */}
        <section className="py-24 px-6 lg:px-12 bg-[#FCFAF6] border-y border-[#E6D5B8]/30" data-aos="fade-up">
          <div className="container mx-auto max-w-5xl text-center">
            <h2 className="text-3xl font-bold text-[#302621] mb-4">Persyaratan Dasar</h2>
            <p className="text-[#7A695B] mb-12 max-w-2xl mx-auto">Untuk mempercepat proses pengajuan, pastikan Anda telah menyiapkan beberapa dokumen administrasi dasar berikut ini.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
              {[
                { title: "KTP Direktur / Penanggung Jawab", desc: "Fotokopi atau scan KTP dari penanggung jawab." },
                { title: "Email, Nomor HP & Tanda Tangan Digital", desc: "Alamat email aktif, nomor telepon/WhatsApp, dan tanda tangan digital untuk keperluan administrasi." },
                { title: "NPWP & NIB (Khusus Badan Usaha/PT)", desc: "Dokumen ini khusus untuk calon customer yang perusahaannya sudah berdiri (PT/CV). Meliputi NPWP Perusahaan dan Nomor Induk Berusaha (OSS)." },
                { title: "Surat Kuasa & Kontrak Maklon", desc: "Surat penunjukan resmi kepada Satala Dermatech (format dokumen akan kami sediakan)." },
                { title: "Data Merek & Desain", desc: "Logo brand, nama produk, dan konsep desain kemasan (kami bantu jika belum ada)." }
              ].map((doc, idx) => (
                <div key={idx} className="flex gap-4 p-6 bg-white border border-[#E6D5B8]/40 rounded-2xl shadow-sm hover:shadow-md transition-shadow group">
                  <div className="mt-1 shrink-0 w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-white transition-colors">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#302621] text-base mb-1">{doc.title}</h4>
                    <p className="text-sm text-[#7A695B] leading-relaxed">{doc.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-6 lg:px-12 relative" data-aos="fade-up">
          <div className="container mx-auto max-w-5xl bg-[#302621] rounded-[2rem] p-10 md:p-16 text-center relative overflow-hidden shadow-2xl">
            {/* Background Accent */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/20 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#D4AF37]/10 rounded-full blur-[60px] translate-y-1/2 -translate-x-1/2"></div>
            
            <div className="relative z-10 max-w-2xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Siap Mengurus Legalitas Produk Anda?</h2>
              <p className="text-[#E6D5B8] mb-10 leading-relaxed font-light">Jangan biarkan birokrasi menghambat peluncuran produk Anda. Mari diskusikan kebutuhan legalitas brand Anda bersama tim expert kami.</p>
              
              <Button size="lg" className="bg-[#D4AF37] hover:bg-[#B58B22] text-white px-10 py-6 text-lg rounded-xl shadow-lg border-0 transition-transform hover:-translate-y-1 w-full sm:w-auto font-bold">
                Mulai Konsultasi Sekarang <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </section>

      </main>

      <PublicFooter />
    </div>
  );
}
